import { useCallback } from 'react'
import {
	ApiDeleteWithToken,
  ApiGetNoToken,
  ApiGetWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiPost,
	useFetch,
} from 'shared/api'
import {
	agreementApi,
	openAgreementApi
} from 'interface/endpoints'
import {
  AgreementCheckResult,
	AgreementResult,
	AgreementResultList,
	AgreementFormData,
	AgreementDraftStateFormData
} from './types'

export * from './types'

export const useCheckAgreement = () => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<AgreementCheckResult>(
        apiAccess,
        agreementApi('/check'),
        cancelToken
      )
    },
		[]
	)
)

export const useGetCurrentAgreementOpen = (
	domain: string
) => useFetch(
	useCallback(
		(cancelToken) => {
      return ApiGetNoToken<AgreementResult>(
        openAgreementApi(domain),
        cancelToken
  		)
    },
		[domain]
	)
)

export const useGetCurrentAgreement = () => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<AgreementResult>(
        apiAccess,
        agreementApi('/current'),
        cancelToken
  		)
    },
		[]
	)
)

export const usePostAgree = (
  callback?: {
    onPosted?: (result: string) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, string>(
	useCallback(
		(apiAccess, cancelToken, version, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        agreementApi(`/${version}/agree`),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useListAgreementVersion = () => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<AgreementResultList>(
        apiAccess,
        agreementApi('/versions'),
        cancelToken
      )
    },
		[]
	)
)

export const usePostAgreement = (
  callback?: {
    onPosted?: (result: AgreementResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<AgreementFormData, AgreementResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        agreementApi('/'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const usePostDraftState = (
  callback?: {
    onPosted?: (result: AgreementResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<{ version: number, form: AgreementDraftStateFormData }, AgreementResult>(
	useCallback(
		(apiAccess, cancelToken, { version, form }, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        agreementApi(`/${version}/draft`),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useRemoveAgreement = (
  callback?: {
    onRemoved?: (result: AgreementResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, AgreementResult>(
	useCallback(
		(apiAccess, cancelToken, version, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        agreementApi(`/${version}`),
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onRemoved) callback.onRemoved(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)
